import { Appointment } from '../appointments';
import { AppointmentStatus } from './AppointmentStatus';
import { ReferralConsentStatus } from './ReferralConsentStatus';

export enum ReferralStatus {
  // Requested, but not yet released into the flow: we are still to hear from
  // the patient. WHY it is held is not encoded here — the referral's
  // `consent.patient.referral` says which consent is `pending` and how it is
  // being obtained (see ReferralConsentSchema).
  requested = 'requested',
  initiated = 'initiated',
  sent = 'sent',
  // The member has created their Member Platform account for this referral
  // but not booked yet (PPL-4196). Still awaiting a booking, so everything
  // that chases a `sent` referral (reminders, auto-cancellation, pausing)
  // chases this one too.
  account_created = 'account_created',
  booked = 'booked',
  appointment_cancelled = 'appointment_cancelled',
  paused = 'paused',
  cancelled = 'cancelled',
  completed = 'completed',
}

// A referral we still have to ask the patient about starts as "requested" and
// moves to "initiated" only once their consent is given. Keyed on the consent
// STATUS, not on its presence: every referral carries a consent section, and
// only the telephone-call evidence leaves it `pending` (see
// ReferralConsentSchema). Structurally typed to keep the enums free of schema
// imports.
export const getInitialReferralStatus = (
  consent:
    { patient?: { referral?: { status?: ReferralConsentStatus } } } | undefined,
) =>
  consent?.patient?.referral?.status === ReferralConsentStatus.pending
    ? ReferralStatus.requested
    : ReferralStatus.initiated;

export function displayReferralStatus(value: ReferralStatus) {
  switch (value) {
    case ReferralStatus.requested:
      return 'Requested';
    case ReferralStatus.initiated:
      return 'Initiated';
    case ReferralStatus.sent:
      return 'Sent';
    case ReferralStatus.account_created:
      return 'Account created';
    case ReferralStatus.booked:
      return 'Booked';
    case ReferralStatus.appointment_cancelled:
      return 'Appointment cancelled';
    case ReferralStatus.paused:
      return 'Paused';
    case ReferralStatus.cancelled:
      return 'Cancelled';
    case ReferralStatus.completed:
      return 'Completed';
    default:
      // A status added after this bundle was built (an EBP tab left open
      // across a deploy reads the new value live): show it as stored rather
      // than throwing, which would take the whole referral list down
      return String(value);
  }
}

export const convertAppointmentToReferralStatus = (
  appointments: Appointment[],
) => {
  if (appointments.length === 0) {
    return null;
  }

  if (
    appointments.some(
      (apt) => apt.status === AppointmentStatus.completed && !apt.noShow,
    )
  ) {
    return ReferralStatus.completed;
  } else if (
    appointments.some(
      (apt) =>
        apt.status === AppointmentStatus.booked ||
        apt.status === AppointmentStatus.rescheduled,
    )
  ) {
    return ReferralStatus.booked;
  } else {
    return ReferralStatus.appointment_cancelled;
  }
};

export const canReferralBePaused = (status: ReferralStatus) => {
  if (
    [
      ReferralStatus.initiated,
      ReferralStatus.sent,
      ReferralStatus.account_created,
      ReferralStatus.appointment_cancelled,
    ].includes(status)
  ) {
    return { allow: true, error: undefined };
  }

  return {
    allow: false,
    error:
      'referral can be paused only in the "initiated", "sent", "account_created" and "appointment_cancelled" statuses',
  };
};
